import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-prayer",
  title: "8 Bible verses about prayer, and what each one asks for",
  h1: "Bible verses about prayer",
  description:
    "Eight passages from the World English Bible with a note on each — including the two that are mainly about how not to do it, which are the most practical in the set.",
  targets: [
    "bible verses about prayer",
    "scripture about praying",
    "what does the bible say about prayer",
  ],
  published: "2026-09-03",
  standfirst:
    "Scripture says less about technique than people expect and more about persistence, privacy and asking for specific things.",
  takeaways: [
    "Two of the most quoted passages about prayer are corrections: Matthew 6:6 against performing it and Matthew 6:7 against volume.",
    "Pray without ceasing is a posture rather than a schedule — the Greek is used elsewhere of a persistent cough.",
    "Luke 18:1 introduces a parable whose point is stated before it starts: keep going and do not give up. The parable's judge is deliberately unflattering.",
    "James 5:16 puts confession and prayer in the same sentence, which is a considerably more social picture of it than most people arrive with.",
  ],
  intro: [
    {
      type: "p",
      text: "Prayer is ninth on the most-searched topic list of the largest topical Bible site, and the search behind it is usually practical — how, how often, and whether it is working. The eight below are the passages that keep coming up, with a note on each about what it is actually asking for.",
    },
    {
      type: "p",
      text: "Two of them are corrections rather than instructions, which is worth knowing in advance. Jesus's most extended teaching about prayer spends its opening on how not to do it, and both warnings are about audience: praying to be seen, and praying at length to be heard.",
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: "But you, when you pray, enter into your inner room, and having shut your door, pray to your Father who is in secret; and your Father who sees in secret will reward you openly.",
        ref: "Matthew 6:6",
      },
      note: "A correction rather than a rule about location — the same sermon assumes public prayer, and Jesus prays in front of crowds elsewhere. What is being removed is the audience. The inner room was the storeroom of an ordinary house, the one space with a door, which makes the instruction domestic and achievable rather than monastic.",
    },
    {
      verse: {
        text: "In praying, don't use vain repetitions as the Gentiles do; for they think that they will be heard for their much speaking. Therefore don't be like them, for your Father knows what things you need before you ask him.",
        ref: "Matthew 6:7–8",
      },
      note: "Aimed at people praying too much rather than too little, which makes it unexpectedly comforting. If you are heard on the basis of volume, having nothing to say is a catastrophe; if you are heard by someone who already knows, a short and empty-handed prayer is not a lesser one. The Lord's Prayer follows immediately, and its brevity is the demonstration.",
    },
    {
      verse: {
        text: "Pray without ceasing.",
        ref: "1 Thessalonians 5:17",
      },
      note: "Two words in Greek and impossible as a schedule, which is a clue that it is not one. The adverb is used elsewhere of something persistent and recurring rather than literally continuous — a cough, a memory. It describes a posture of returning rather than a requirement to be speaking, and it sits in a run of short instructions about ordinary life.",
    },
    {
      verse: {
        text: "He also spoke a parable to them that they must always pray and not give up,",
        ref: "Luke 18:1",
      },
      note: "Unusually, the point is stated before the parable rather than after it. What follows is the story of a widow wearing down an unjust judge who has no respect for anybody, and the argument runs from the worse case to the better: if persistence works on him, it is not wasted here. The judge is deliberately unflattering, which is what stops the parable being sentimental.",
    },
    {
      verse: {
        text: "Confess your sins to one another and pray for one another, that you may be healed. The insistent prayer of a righteous person is powerfully effective.",
        ref: "James 5:16",
      },
      note: "Confession and prayer arrive in the same sentence, addressed to a congregation rather than to an individual, which is a much more social picture of prayer than most people bring to it. The word translated insistent carries the sense of energy at work rather than of emotional intensity — the claim is about effect rather than about how hard anybody is trying.",
    },
    {
      verse: {
        text: "This is the boldness which we have toward him, that if we ask anything according to his will, he listens to us.",
        ref: "1 John 5:14",
      },
      note: "The qualifier — according to his will — is often read as a get-out clause, and in the letter it is doing the opposite. The whole point being made is confidence: you may ask boldly, and the boundary is not your worthiness or your wording. It is a claim about being listened to rather than a formula for guaranteed answers.",
    },
    {
      verse: {
        text: "Continue steadfastly in prayer, watching in it with thanksgiving,",
        ref: "Colossians 4:2",
      },
      note: "Three things in one short instruction, and the middle one is the one usually dropped. Watching means staying alert for the answer — the same idea as the second half of Psalm 5:3, where the psalmist lays out the requests and then watches expectantly. A prayer nobody ever looks for an answer to is very easy to stop saying.",
    },
    {
      verse: {
        text: "Yahweh is near to all those who call on him, to all who call on him in truth.",
        ref: "Psalm 145:18",
      },
      note: "Near, which is a claim about location rather than about outcome, and the qualifier is honesty rather than eloquence. In truth here means without pretence — the psalm is not asking for better wording but for the real request. That is the same instruction Matthew 6:7 gives from the other direction.",
    },
    {
      verse: {
        text: "Let's therefore draw near with boldness to the throne of grace, that we may receive mercy and may find grace for help in time of need.",
        ref: "Hebrews 4:16",
      },
      note: "Boldness is a public-speaking word — the freedom of a citizen to speak plainly in an assembly. What is being described is not confidence in your own standing but permission to speak without hedging, and the reason given in the previous verse is that the one being addressed has been through the same things you have.",
    },
    {
      verse: {
        text: "The righteous cry, and Yahweh hears, and delivers them out of all their troubles.",
        ref: "Psalm 34:17",
      },
      note: "Read alongside verse 19 of the same psalm, which says the afflictions of the righteous are many — the two sit fifteen lines apart and are meant to be held together. The psalm is not claiming a life without trouble; it is claiming that the crying is heard, which is a different and considerably more defensible promise.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "what-these-add-up-to",
      text: "What these add up to",
    },
    {
      type: "p",
      text: "Short, specific, honest, repeated, and looked back on. That is nearly the whole of the New Testament's practical teaching about prayer, and none of it is about technique.",
    },
    {
      type: "p",
      text: "If the difficulty is not method but that nothing comes at all, that is common enough to have its own literature — and {a prayer for the weeks when there are no words} is a better starting point than a longer list of verses.",
      links: {
        "a prayer for the weeks when there are no words":
          "/prayers/prayer-when-you-cant-pray",
      },
    },
    {
      type: "p",
      text: "It is also worth noticing what is absent. There is no prescribed posture, no required opening or closing formula, no minimum duration, and no instruction about where in the day it should happen. The one place a length is discussed, the direction of the correction is downward. For a religion with a reputation for ritual, the practical teaching here is remarkably bare.",
    },
    {
      type: "note",
      text: "Colossians 4:2 is the one most people are missing. Ask, then actually watch for the answer during the week — otherwise you will never notice one.",
    },
  ],
  faq: [
    {
      q: "What does the Bible say about how to pray?",
      a: "Less about technique than people expect. Privately rather than for an audience, briefly rather than at length, specifically, persistently, and while watching for the answer.",
    },
    {
      q: "What does pray without ceasing mean?",
      a: "The Greek adverb describes something persistent and recurring rather than literally continuous — it is used elsewhere of a cough. It describes a posture of returning rather than a requirement to be speaking constantly.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so all ten are printed whole rather than referred to. Psalm 145:18 and Psalm 34:17 render the divine name as Yahweh, where most translations have 'the LORD'.",
    },
  ],
  related: ["morning-quiet-time-routine", "how-to-memorize-bible-verses"],
  relatedTopics: ["bible-verses-about-peace", "bible-verses-about-time"],
};
